'use client';
import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Mail, Lock, User, Building2, Phone, LogIn, UserPlus, Globe } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

type Role = 'customer' | 'operator';
type Mode = 'login' | 'signup';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupCustomerForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

interface SignupOperatorForm {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  region: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

export default function AuthScreen() {
  const [role, setRole] = useState<Role>('customer');
  const [mode, setMode] = useState<Mode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const router = useRouter();
  const { signIn, signUp } = useAuth();

  const loginForm = useForm<LoginForm>();
  const signupCustomerForm = useForm<SignupCustomerForm>();
  const signupOperatorForm = useForm<SignupOperatorForm>();

  // Restore persisted cooldown on mount (survives page refresh)
  useEffect(() => {
    const stored = localStorage.getItem('auth_cooldown_until');
    if (stored) {
      const remaining = Math.ceil((parseInt(stored, 10) - Date.now()) / 1000);
      if (remaining > 0) {
        setCooldown(remaining);
      } else {
        localStorage.removeItem('auth_cooldown_until');
      }
    }
  }, []);

  // Tick the cooldown counter every second
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          localStorage.removeItem('auth_cooldown_until');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const getRoleRedirect = (userRole?: string): string => {
    if (userRole === 'admin') return '/admin-panel';
    if (userRole === 'operator') return '/operator-dashboard';
    return '/safari-packages-listing';
  };

  const startCooldown = (seconds: number) => {
    const until = Date.now() + seconds * 1000;
    localStorage.setItem('auth_cooldown_until', String(until));
    setCooldown(seconds);
  };

  const isRateLimitError = (error: any): boolean => {
    const msg: string = (error?.message || '').toLowerCase();
    const code: string = (error?.code || '').toLowerCase();
    const status: number = error?.status || 0;
    return (
      msg.includes('rate limit') ||
      msg.includes('too many') ||
      msg.includes('too many requests') ||
      code.includes('over_request_rate_limit') ||
      code.includes('rate_limit') ||
      status === 429
    );
  };

  const getFriendlyError = (error: any): string => {
    const msg: string = error?.message || '';
    if (isRateLimitError(error)) {
      startCooldown(60);
      return 'Too many attempts. Please wait 60 seconds before trying again.';
    }
    if (msg.toLowerCase().includes('email not confirmed')) {
      return 'Please check your email and confirm your account before signing in.';
    }
    if (msg.toLowerCase().includes('invalid login credentials') || msg.toLowerCase().includes('invalid credentials')) {
      return 'Incorrect email or password. Please try again.';
    }
    if (msg.toLowerCase().includes('user already registered')) {
      return 'An account with this email already exists. Please sign in instead.';
    }
    return msg || 'Something went wrong. Please try again.';
  };

  const handleLogin = async (data: LoginForm) => {
    if (cooldown > 0) return;
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      toast.success('Welcome back! Redirecting...');
      const userRole = result?.user?.user_metadata?.role;
      const redirect = getRoleRedirect(userRole);
      router.push(redirect);
    } catch (error: any) {
      setIsLoading(false);
      const message = getFriendlyError(error);
      loginForm.setError('email', { message });
      toast.error(message);
    }
  };

  const handleCustomerSignup = async (data: SignupCustomerForm) => {
    if (cooldown > 0) return;
    if (data.password !== data.confirmPassword) {
      signupCustomerForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    try {
      // Capture referral code from URL before sign-up
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');

      await signUp(data.email, data.password, {
        fullName: `${data.firstName} ${data.lastName}`,
        phone: data.phone,
        role: 'customer',
      });

      // Process referral if a code was present
      if (refCode) {
        try {
          const { createClient } = await import('@/lib/supabase/client');
          const supabase = createClient();
          // Wait briefly for the trigger to create user_profiles row
          await new Promise((r) => setTimeout(r, 1200));
          const { data: { user: newUser } } = await supabase.auth.getUser();
          if (newUser) {
            await supabase.rpc('process_referral_signup', {
              p_referred_user_id: newUser.id,
              p_referral_code: refCode,
            });
          }
        } catch {
          // Referral processing failure is non-blocking
        }
      }

      setIsLoading(false);
      toast.success('Account created! You can now sign in.');
      setMode('login');
      loginForm.setValue('email', data.email);
    } catch (error: any) {
      setIsLoading(false);
      const message = getFriendlyError(error);
      toast.error(message);
    }
  };

  const handleOperatorSignup = async (data: SignupOperatorForm) => {
    if (cooldown > 0) return;
    if (data.password !== data.confirmPassword) {
      signupOperatorForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.contactName,
        companyName: data.companyName,
        phone: data.phone,
        region: data.region,
        role: 'operator',
      });
      setIsLoading(false);
      toast.success('Operator application submitted! Your account is pending review.');
      setMode('login');
    } catch (error: any) {
      setIsLoading(false);
      const message = getFriendlyError(error);
      toast.error(message);
    }
  };

  const ROLE_TABS: { id: Role; label: string; icon: React.ElementType; desc: string }[] = [
    { id: 'customer', label: 'Traveler', icon: User, desc: 'Book safaris and manage trips' },
    { id: 'operator', label: 'Operator', icon: Building2, desc: 'Manage packages and bookings' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Brand Hero */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden bg-safari-green flex-col">
        <div className="absolute inset-0">
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_16d806e5f-1772249664669.png"
            alt="Wildebeest crossing Mara River during Great Migration at golden sunset"
            fill
            className="object-cover opacity-40"
            priority
            sizes="60vw"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-safari-green/90 via-safari-green/70 to-safari-green/50"></div>
        </div>

        <div className="relative z-10 flex flex-col h-full p-10 xl:p-14">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div>
              <p className="text-sm font-bold text-safari-yellow tracking-widest uppercase leading-none">Onthego</p>
              <p className="text-xl font-bold text-white leading-none">Safari</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 bg-safari-yellow/20 border border-safari-yellow/30 text-safari-yellow text-xs font-semibold px-3 py-1.5 rounded-full mb-6 w-fit">
              <span className="w-1.5 h-1.5 bg-safari-yellow rounded-full"></span>
              East Africa&apos;s Premier Safari Platform
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
              Your Safari<br />
              <span className="text-safari-yellow">Adventure Awaits</span>
            </h1>
            <p className="text-white/70 text-lg mb-10 max-w-md">
              From the Maasai Mara to Bwindi — discover, book, and experience the wild heart of Africa with Onthego Safari.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { value: '150+', label: 'Safari Packages' },
                { value: '38', label: 'Verified Operators' },
                { value: '12K+', label: 'Happy Travelers' },
              ].map((stat) => (
                <div key={`stat-${stat.label}`} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                  <p className="text-2xl font-bold text-safari-yellow tabular-nums">{stat.value}</p>
                  <p className="text-xs text-white/60 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom testimonial */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
            <p className="text-white/90 text-sm italic mb-3">
              &ldquo;Booked the Maasai Mara package through Onthego Safari — the deposit system made it so easy to plan ahead. Absolutely incredible experience.&rdquo;
            </p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-safari-yellow rounded-full flex items-center justify-center text-gray-900 font-bold text-sm">P</div>
              <div>
                <p className="text-white text-sm font-semibold">Priya Nair</p>
                <p className="text-white/50 text-xs">Verified Traveler · Mumbai, India</p>
              </div>
              <div className="ml-auto flex gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <span key={`star-${s}`} className="text-safari-yellow text-xs">★</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel — Auth Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col overflow-y-auto">
        <div className="flex-1 flex flex-col justify-center px-6 py-10 sm:px-10 xl:px-14 max-w-lg mx-auto w-full">
          {/* Mobile Logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <span className="font-bold text-lg text-safari-green">
              Onthego <span className="text-safari-yellow">Safari</span>
            </span>
          </div>

          {/* Heading */}
          <div className="mb-7">
            <h2 className="text-2xl font-bold text-gray-900">
              {mode === 'login' ? 'Sign in to your account' : 'Create your account'}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {mode === 'login' ? 'Welcome back — select your role and sign in.' : 'Join Onthego Safari and start exploring.'}
            </p>
          </div>

          {/* Role Tabs */}
          <div className="flex gap-2 mb-6 p-1 bg-gray-100 rounded-xl">
            {ROLE_TABS.map((tab) => (
              <button
                key={`role-tab-${tab.id}`}
                onClick={() => setRole(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold rounded-lg transition-all ${
                  role === tab.id ? 'bg-white text-safari-green shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon size={13} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Role description */}
          <p className="text-xs text-gray-400 mb-5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 bg-safari-yellow rounded-full"></span>
            {ROLE_TABS.find((t) => t.id === role)?.desc}
          </p>

          {/* LOGIN FORM */}
          {mode === 'login' && (
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4 fade-in">
              {/* Social Auth (customer only) */}
              {role === 'customer' && (
                <button
                  type="button"
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all"
                >
                  <Globe size={16} className="text-blue-500" />
                  Continue with Google
                </button>
              )}

              {role === 'customer' && (
                <div className="flex items-center gap-3 my-2">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <span className="text-xs text-gray-400 font-medium">or sign in with email</span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Email Address</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    {...loginForm.register('email', { required: 'Email is required' })}
                    type="email"
                    placeholder={role === 'operator' ? 'yourname@company.co.ke' : role === 'admin' ? 'admin@onthego.co.ke' : 'you@example.com'}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl text-sm font-medium text-gray-800 outline-none transition-all ${
                      loginForm.formState.errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-safari-green'
                    }`}
                  />
                </div>
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Password</label>
                  <button type="button" className="text-xs text-safari-green font-medium hover:text-green-700">
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    {...loginForm.register('password', { required: 'Password is required' })}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className={`w-full pl-10 pr-11 py-3 border rounded-xl text-sm font-medium text-gray-800 outline-none transition-all ${
                      loginForm.formState.errors.password ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-safari-green'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-red-500 mt-1">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  {...loginForm.register('remember')}
                  type="checkbox"
                  id="remember"
                  className="accent-safari-green w-4 h-4"
                />
                <label htmlFor="remember" className="text-sm text-gray-600 cursor-pointer">Remember me for 30 days</label>
              </div>

              <button
                type="submit"
                disabled={isLoading || cooldown > 0}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-safari-green text-white font-bold rounded-xl hover:bg-green-800 transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Signing in...
                  </>
                ) : cooldown > 0 ? (
                  <>Please wait {cooldown}s before retrying</>
                ) : (
                  <>
                    <LogIn size={16} />
                    Sign In as {ROLE_TABS.find((t) => t.id === role)?.label}
                  </>
                )}
              </button>

              <p className="text-center text-sm text-gray-500">
                Don&apos;t have an account?{' '}
                <button
                  type="button"
                  onClick={() => setMode('signup')}
                  className="text-safari-green font-semibold hover:text-green-700"
                >
                  Create one
                </button>
              </p>
            </form>
          )}

          {/* SIGNUP FORM — Customer */}
          {mode === 'signup' && role === 'customer' && (
            <form onSubmit={signupCustomerForm.handleSubmit(handleCustomerSignup)} className="space-y-4 fade-in">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">First Name *</label>
                  <input
                    {...signupCustomerForm.register('firstName', { required: true })}
                    placeholder="Amara"
                    className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Last Name *</label>
                  <input
                    {...signupCustomerForm.register('lastName', { required: true })}
                    placeholder="Osei"
                    className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Email Address *</label>
                <input
                  {...signupCustomerForm.register('email', { required: true })}
                  type="email"
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Phone Number *</label>
                <input
                  {...signupCustomerForm.register('phone', { required: true })}
                  type="tel"
                  placeholder="+254 7XX XXX XXX"
                  className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Password *</label>
                <div className="relative">
                  <input
                    {...signupCustomerForm.register('password', { required: true, minLength: { value: 8, message: 'Min 8 characters' } })}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min 8 characters"
                    className="w-full pr-11 px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {signupCustomerForm.formState.errors.password && (
                  <p className="text-xs text-red-500 mt-1">{signupCustomerForm.formState.errors.password.message}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Confirm Password *</label>
                <div className="relative">
                  <input
                    {...signupCustomerForm.register('confirmPassword', { required: true })}
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Re-enter password"
                    className="w-full pr-11 px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                    {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {signupCustomerForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">{signupCustomerForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>
              <div className="flex items-start gap-2">
                <input
                  {...signupCustomerForm.register('terms', { required: true })}
                  type="checkbox"
                  id="terms-customer"
                  className="accent-safari-green w-4 h-4 mt-0.5"
                />
                <label htmlFor="terms-customer" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
                  I agree to the <span className="text-safari-green font-medium">Terms of Service</span> and <span className="text-safari-green font-medium">Privacy Policy</span>
                </label>
              </div>
              <button
                type="submit"
                disabled={isLoading || cooldown > 0}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-safari-yellow text-gray-900 font-bold rounded-xl hover:bg-yellow-400 transition-all active:scale-[0.98] disabled:opacity-70"
              >
                {isLoading ? (
                  <><div className="w-4 h-4 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin"></div>Creating account...</>
                ) : cooldown > 0 ? (
                  <>Please wait {cooldown}s before retrying</>
                ) : (
                  <><UserPlus size={16} />Create Traveler Account</>
                )}
              </button>
              <p className="text-center text-sm text-gray-500">
                Already have an account?{' '}
                <button type="button" onClick={() => setMode('login')} className="text-safari-green font-semibold hover:text-green-700">
                  Sign in
                </button>
              </p>
            </form>
          )}

          {/* SIGNUP FORM — Operator */}
          {mode === 'signup' && role === 'operator' && (
            <form onSubmit={signupOperatorForm.handleSubmit(handleOperatorSignup)} className="space-y-4 fade-in">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 font-medium">
                Operator accounts require platform review (1–2 business days) before activation.
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Company Name *</label>
                <input
                  {...signupOperatorForm.register('companyName', { required: true })}
                  placeholder="Savanna Adventures Ltd."
                  className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Contact Name *</label>
                  <input
                    {...signupOperatorForm.register('contactName', { required: true })}
                    placeholder="James Kamau"
                    className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Phone *</label>
                  <input
                    {...signupOperatorForm.register('phone', { required: true })}
                    type="tel"
                    placeholder="+254 7XX XXX XXX"
                    className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Business Email *</label>
                <input
                  {...signupOperatorForm.register('email', { required: true })}
                  type="email"
                  placeholder="contact@yourcompany.co.ke"
                  className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Operating Region *</label>
                <select
                  {...signupOperatorForm.register('region', { required: true })}
                  className="w-full px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-700 outline-none transition-all bg-white"
                >
                  <option value="">Select primary region</option>
                  {['Maasai Mara, Kenya', 'Serengeti, Tanzania', 'Amboseli, Kenya', 'Tsavo, Kenya', 'Ngorongoro, Tanzania', 'Samburu, Kenya', 'Bwindi, Uganda', 'Murchison Falls, Uganda', 'Other'].map((r) => (
                    <option key={`region-opt-${r}`} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Password *</label>
                <div className="relative">
                  <input
                    {...signupOperatorForm.register('password', { required: true })}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min 8 characters"
                    className="w-full pr-11 px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Confirm Password *</label>
                <div className="relative">
                  <input
                    {...signupOperatorForm.register('confirmPassword', { required: true })}
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Re-enter password"
                    className="w-full pr-11 px-4 py-3 border border-gray-200 focus:border-safari-green rounded-xl text-sm font-medium text-gray-800 outline-none transition-all"
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                    {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {signupOperatorForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">{signupOperatorForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>
              <div className="flex items-start gap-2">
                <input {...signupOperatorForm.register('terms', { required: true })} type="checkbox" id="terms-op" className="accent-safari-green w-4 h-4 mt-0.5" />
                <label htmlFor="terms-op" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
                  I agree to the <span className="text-safari-green font-medium">Operator Terms</span>, <span className="text-safari-green font-medium">Commission Policy</span>, and <span className="text-safari-green font-medium">Privacy Policy</span>
                </label>
              </div>
              <button
                type="submit"
                disabled={isLoading || cooldown > 0}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-safari-yellow text-gray-900 font-bold rounded-xl hover:bg-yellow-400 transition-all active:scale-[0.98] disabled:opacity-70"
              >
                {isLoading ? (
                  <><div className="w-4 h-4 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin"></div>Submitting...</>
                ) : cooldown > 0 ? (
                  <>Please wait {cooldown}s before retrying</>
                ) : (
                  <><Building2 size={16} />Apply as Safari Operator</>
                )}
              </button>
              <p className="text-center text-sm text-gray-500">
                Already registered?{' '}
                <button type="button" onClick={() => setMode('login')} className="text-safari-green font-semibold hover:text-green-700">Sign in</button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}